import {
  CUSTOM_PREFIX,
  managedProperties,
  newRow,
  type PropertyRow,
  type RowErrors,
} from './customProperties';

/**
 * Add / edit / remove a resource's `custom.*` properties (#37). The
 * `custom.` prefix is fixed text, so no other namespace can be typed;
 * validation lives in ./customProperties (shared-schema backed) and its
 * errors are passed in, shown once the user has tried to save.
 */
export function PropertiesEditor({
  idPrefix,
  rows,
  onChange,
  errors,
  managed = {},
}: {
  idPrefix: string;
  rows: PropertyRow[];
  onChange: (rows: PropertyRow[]) => void;
  errors: Record<number, RowErrors>;
  /** Read-only system.* / auto.* entries, shown beneath the editor. */
  managed?: Record<string, string>;
}) {
  const update = (id: number, patch: Partial<PropertyRow>) =>
    onChange(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const managedEntries = managedProperties(managed);

  return (
    <fieldset className="form-field properties-editor">
      <legend>Custom properties</legend>
      <p className="text-muted">
        Properties drive which sensors apply (for example, mail and AI-ownership
        checks use <code>custom.mail_domain</code>). Saving re-evaluates sensor
        assignments automatically.
      </p>
      {rows.map((row, i) => {
        const err = errors[row.id] ?? {};
        const n = i + 1;
        return (
          <div className="properties-row" key={row.id}>
            <div className="properties-row-fields">
              <span className="properties-prefix" aria-hidden="true">
                {CUSTOM_PREFIX}
              </span>
              <input
                id={`${idPrefix}-name-${row.id}`}
                aria-label={`Property ${n} name`}
                aria-invalid={err.name !== undefined}
                aria-describedby={err.name ? `${idPrefix}-name-${row.id}-err` : undefined}
                placeholder="mail_domain"
                value={row.name}
                onChange={(e) => update(row.id, { name: e.target.value })}
              />
              <input
                id={`${idPrefix}-value-${row.id}`}
                aria-label={`Property ${n} value`}
                aria-invalid={err.value !== undefined}
                aria-describedby={err.value ? `${idPrefix}-value-${row.id}-err` : undefined}
                placeholder="example.com"
                value={row.value}
                onChange={(e) => update(row.id, { value: e.target.value })}
              />
              <button
                type="button"
                className="btn btn-ghost"
                aria-label={`Remove property ${n}`}
                onClick={() => onChange(rows.filter((r) => r.id !== row.id))}
              >
                ✕
              </button>
            </div>
            {err.name && (
              <p className="field-error" role="alert" id={`${idPrefix}-name-${row.id}-err`}>
                {err.name}
              </p>
            )}
            {err.value && (
              <p className="field-error" role="alert" id={`${idPrefix}-value-${row.id}-err`}>
                {err.value}
              </p>
            )}
          </div>
        );
      })}
      <button
        type="button"
        className="btn btn-secondary"
        onClick={() => onChange([...rows, newRow()])}
      >
        Add property
      </button>
      {managedEntries.length > 0 && (
        <div className="properties-managed">
          <p className="text-muted">Managed by Detect (read-only):</p>
          <ul>
            {managedEntries.map(([key, value]) => (
              <li key={key}>
                <code>{key}</code> = {value}
              </li>
            ))}
          </ul>
        </div>
      )}
    </fieldset>
  );
}
